import { useCallback, useState } from 'react';
import request from 'lib/request';
import type { AliasedClusterResponse, AliasEdge } from 'types/api-responses';
import type { AliasClusterMember } from './alias-cluster-graph';

// Lazily fetches an entity's alias cluster (GET /entities/:id/aliased_entities)
// and maps it to AliasClusterGraph members + the actual directed edges among
// them. Lazy because the graph is only needed when the user clicks an alias
// pill — we don't fetch on render.
export function useAliasCluster(entityId: number) {
  const [members, setMembers] = useState<AliasClusterMember[]>([]);
  const [edges, setEdges] = useState<AliasEdge[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchCluster = useCallback(async () => {
    if (loaded || loading) return;
    setLoading(true);
    const response = await request.get<AliasedClusterResponse>(
      `/entities/${entityId}/aliased_entities`
    );
    if (!('error' in response)) {
      setMembers(
        (response.members ?? []).map((row) => ({
          name: row.entity.name,
          entityId: String(row.entity.id),
          entityType: row.entity.type,
          isPrimary: row.canonical,
          flagCategory: row.flagCategory ?? null,
          flagRationale: row.flagRationale ?? null,
          flagSeverity: row.flagSeverity ?? null,
        }))
      );
      setEdges(response.edges ?? []);
      setLoaded(true);
    }
    setLoading(false);
  }, [entityId, loaded, loading]);

  return { members, edges, loading, fetchCluster };
}
